<template>
  <div>
    <b-field
      :label="label"
      :message="msgError"
      :type="msgError ? 'is-warning' : ''">
      <b-autocomplete
        :value="search"
        :data="itemsFiltered"
        :loading="isFetching"
        :placeholder="placeholder"
        :open-on-focus="openOnFocus || advancedSearch"
        :disabled="disabled"
        :check-infinite-scroll="true"
        :append-to-body="appendToBodyCalc"
        :expanded="expanded"
        @input="getItems"
        @select="setItem"
        @infinite-scroll="getMoreItems"
        @click.native="$emit('click-autocomplete', $event)"
        :field="field"
        icon="magnify"
        ref="tiersAutocomplete">
        <slot v-for="(_, name) in $slots" :name="name" :slot="name" />
        <template v-for="(_, name) in $scopedSlots" v-slot:[name]="data">
          <slot :name="name" v-bind="data"/>
        </template>
        <template #header v-if="!$slots.header">
          <a
            v-if="advancedSearch"
            @click.stop="openAdvancedSearch"
            class="lien is-inline-block">
            {{ $t('general.autocomplete.advancedSearch') }}
          </a>
        </template>
        <template slot="empty">
          <div v-if="!isFetching">
            {{$t('general.autocomplete.noRslt')}}
          </div>
          <div v-else>
            {{$t('general.autocomplete.search')}}
          </div>
        </template>
      </b-autocomplete>
    </b-field>
    <b-modal
      v-model="isAdvancedSearch"
      class="modal"
      width="1400px"
      has-modal-card>
      <popUpAdvencedSearch
        @addClient="(client) => setItem(client)"
        @removeClient="() => setItem(null)"
        :type="type"
        :sousTypes="sousTypes"
        :selections="itemSelected ? [itemSelected] : []"
        :add-contacts="false"
        :title="titlePopUpAdvancedSearch"
        :search-by-competence-fournisseur="searchByCompetenceFournisseur"
        key-id="id"/>
    </b-modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { getAll as getAllTiers } from '@/api/tiers';
import { RESULTS_PER_PAGE } from '@/configuration/clientTableConf';
import messageErreur from '@/services/general/messageErreur';
import adressFullStringGetter from '@/services/general/adressFullStringGetter';
import { LISTE_TYPOLOGIE } from '@/configuration/tiers/tiersConf';
import PopUpAdvencedSearch from '@/components/devisCommandes/editionDevisCommande/p_advancedSearch';

export default {
  name: 'TiersAutocomplete',
  props: {
    label: {
      type: String,
      required: false,
    },
    value: {
      type: Object,
      required: false,
      default: () => {},
    },
    societeActiveId: {
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    sousTypes: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    field: {
      type: String,
      default: 'nomCommercial',
    },
    placeholder: {
      type: String,
      required: false,
    },
    filteredItemFunc: {
      type: Function,
      required: false,
    },
    openOnFocus: {
      type: Boolean,
      default: false,
    },
    filterName: {
      type: String,
      default: 'nomCommercial',
    },
    appendToBody: {
      type: Boolean,
    },
    advancedSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    expanded: {
      type: Boolean,
      required: false,
      default: false,
    },
    titlePopUpAdvancedSearch: {
      type: String,
      required: false,
    },
    searchByCompetenceFournisseur: {
      type: Boolean,
      required: false,
      default: false,
    },
    msgError: {
      type: String,
      required: false,
    },
    injectItems: {
      type: Array,
      required: false,
      default: () => [],
    },
    tiersOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    focus: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    PopUpAdvencedSearch,
  },
  data() {
    return {
      search: null,
      items: [],
      isFetching: false,
      isAdvancedSearch: false,
    };
  },
  computed: {
    itemSelected: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit('input', newValue);
      },
    },
    itemsFiltered() {
      let fItems = this.items
        .filter(el => !this.filteredItemFunc || this.filteredItemFunc(el));
      if (this.injectItems?.length > 0) {
        fItems.unshift(this.injectItems);
        fItems = fItems.flat(2);
      }
      return fItems;
    },
    appendToBodyCalc() {
      if (typeof this.appendToBody === 'boolean') return this.appendToBody;
      return this.expanded;
    },
  },
  methods: {
    async getTiers(text, addMore = false) {
      try {
        this.isFetching = true;
        const paylaod = {
          query: {
            [this.filterName]: text,
            type: this.type,
            sousType: this.sousTypes,
            societeId: this.societeActiveId,
            archive: false,
          },
          limit: RESULTS_PER_PAGE,
        };
        if (addMore && (this.items.length % RESULTS_PER_PAGE === 0)) {
          paylaod.page = this.items.length / RESULTS_PER_PAGE;
        }
        const tiers = (await getAllTiers(paylaod)).data.result.items;
        if (this.tiersOptions.length) {
          const opts = this.tiersOptions
            .filter(o => o[this.filterName].toLowerCase().includes(text.toLowerCase()));
          tiers.unshift(...opts);
        }
        tiers.forEach(this.handleTiers);
        if (addMore) {
          this.items = this.items.concat(tiers);
        } else {
          this.items = tiers;
        }
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        this.isFetching = false;
      }
    },

    getItems: debounce(async function itemsData(text) {
      if (text === this.search) return;
      this.search = text;
      this.itemSelected = null;
      await this.getTiers(text);
    }, 500),

    async getMoreItems() {
      if (this.items.length % RESULTS_PER_PAGE !== 0) return;
      await this.getTiers(this.search, true);
    },

    handleTiers(tiers) {
      const typologie = LISTE_TYPOLOGIE.find((typo) => typo.code === tiers.typologie);
      tiers.typologieLibelle = this.$t(typologie.libelle);
      tiers.adressePostaleLibelle = adressFullStringGetter(tiers.adressePostale, false);
    },

    setItem(val) {
      if (!val) return;
      this.search = val.nomCommercial;
      this.itemSelected = val;
      this.$emit('select', val);
    },
    openAdvancedSearch() {
      this.$refs.tiersAutocomplete.setSelected(this.itemSelected);
      this.isAdvancedSearch = true;
    },
  },
  async mounted() {
    this.search = this.value?.nomCommercial;
    if (this.openOnFocus) await this.getTiers();
    if (this.focus) this.$refs.tiersAutocomplete?.focus();
  },
};
</script>
